import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ButtonWithGAClickButton,
  GalleryModal,
  SectionTitle,
  SwiperWithNavigation,
  SwiperSlide,
} from 'common/components';
import { createElementKey } from 'utils';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { usePromoViewEvent } from 'common/hooks';
import {
  galleryImage,
  gallerySection,
  gallerySectionTitle,
  swiper,
  swiperSlideStyle,
  footerButton,
} from './gallery-section-styles';
import { GalleryListStructure } from './gallery-section-types';

type GallerySectionProps = {
  elementBlock: string;
  galleryList: GalleryListStructure[];
  onConsultationSignupBtnClick: () => void;
};

/**
 * Вью секции с примерами работ
 */
export const GallerySection = ({
  elementBlock,
  galleryList,
  onConsultationSignupBtnClick,
}: GallerySectionProps) => {
  const galleryRef = useRef<HTMLImageElement[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [clickedImageIndex, setClickedImageIndex] = useState(0);
  const promotions = useMemo(
    () =>
      galleryList.map(({ name }, index: number) => ({
        name: `${elementBlock}_bottom`,
        creative: name,
        position: (index + 1).toString(),
      })),
    [elementBlock, galleryList]
  );

  usePromoViewEvent(galleryRef, `${elementBlock} / gallery`, promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent(
      elementBlock,
      ClickButtonElementName.SwitchForward,
      'Галерея выполненных проектов'
    );
  }, [elementBlock]);

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = useCallback(() => {
    analyticsUtils.clickButtonEvent(
      elementBlock,
      ClickButtonElementName.SwitchBackward,
      'Галерея выполненных проектов'
    );
  }, [elementBlock]);

  /**
   * Обработчик нажатия на изображение
   */
  const handleImgClick = useCallback(
    (index: number) => {
      setIsModalVisible(true);
      setClickedImageIndex(index);
      analyticsUtils.promoClickEvent(`${elementBlock} / gallery`, promotions[index]);
    },
    [elementBlock, promotions, setIsModalVisible, setClickedImageIndex]
  );

  /**
   * Обработчик закрытия модального окна с просмотром галереии
   */
  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
  }, [setIsModalVisible]);

  /**
   * ОБработчик нажатия на кнопку "Записаться на проектирование"
   */
  const handleConsultationSignupBtnClick = useCallback(() => {
    setIsModalVisible(false);
    onConsultationSignupBtnClick();
  }, [onConsultationSignupBtnClick, setIsModalVisible]);

  return (
    <section className={gallerySection}>
      <SectionTitle className={gallerySectionTitle}>Галерея выполненных проектов</SectionTitle>
      <SwiperWithNavigation
        className={swiper}
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
        slidesPerView="auto"
        watchOverflow>
        {galleryList.map(({ srcSmall, alt }, index: number) => (
          <SwiperSlide
            className={swiperSlideStyle}
            data-position={index}
            key={createElementKey('gallery', index)}
            onClick={() => handleImgClick(index)}>
            <img
              alt={alt}
              loading="lazy"
              className={galleryImage}
              data-position={index}
              ref={(el: HTMLImageElement) => {
                if (el) galleryRef.current[index] = el;
              }}
              src={srcSmall}
              title={alt}
            />
          </SwiperSlide>
        ))}
      </SwiperWithNavigation>

      <GalleryModal
        clickedImageIndex={clickedImageIndex}
        elementBlock={elementBlock}
        elementText="Галерея выполненных проектов / modal"
        galleryList={galleryList}
        onClose={handleCloseModal}
        show={isModalVisible}
        isShowImageName={false}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            className={footerButton}
            wide
            adaptive={false}
            elementBlock={elementBlock}
            elementName="Галерея выполненных проектов / modal"
            elementText="Записаться на проектирование"
            onClick={handleConsultationSignupBtnClick}
            size="s"
            variant="accent">
            Записаться на проектирование
          </ButtonWithGAClickButton>
        )}
      />
    </section>
  );
};
